import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Clock, RefreshCw, CheckCircle2, XCircle, MinusCircle, Loader2, ChevronDown, ChevronRight, AlertTriangle,
} from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

interface CronRun {
  id: string;
  jobKey: string;
  status: 'RUNNING' | 'SUCCESS' | 'FAILED' | 'SKIPPED';
  startedAt: string;
  finishedAt?: string | null;
  durationMs?: number | null;
  scanned: number;
  affected: number;
  failed: number;
  note?: string | null;
  errorMessage?: string | null;
  triggeredBy: string;
}

interface CronJob {
  key: string;
  name: string;
  schedule: string;
  scheduleText: string;
  module: string;
  description: string;
  affectsLabel: string;
  lastRun: CronRun | null;
  runs24h: number;
  failed24h: number;
  affected24h: number;
  scanned24h: number;
  affected7d: number;
}

const STATUS_STYLE: Record<string, { cls: string; Icon: any }> = {
  SUCCESS: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  FAILED: { cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: XCircle },
  SKIPPED: { cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: MinusCircle },
  RUNNING: { cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Loader2 },
};

function StatusPill({ status }: { status: string }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.SKIPPED;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${s.cls}`}>
      <s.Icon size={11} className={status === 'RUNNING' ? 'animate-spin' : ''} />
      {status}
    </span>
  );
}

const fmtWhen = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

function fmtDuration(ms?: number | null) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

/** "3 hours ago" — the column ops actually reads, since "did it run recently" matters more than the clock time. */
function fmtAgo(v?: string | null) {
  if (!v) return 'Never run';
  const diff = Date.now() - new Date(v).getTime();
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

/**
 * Cron History — SUPER_ADMIN / ADMIN only.
 *
 * Every scheduled job, its schedule, when it last ran, how long it took and how many records it
 * touched. A job that has never run still appears with "Never run", which is the whole point: the
 * job you need to spot is the one that ISN'T firing.
 */
export function CronHistoryPanel() {
  const { user } = useAuth();
  const isAdmin = user?.userType === 'SUPER_ADMIN' || user?.userType === 'ADMIN';
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: jobs = [], isLoading, refetch, isFetching } = useQuery<CronJob[]>({
    queryKey: ['cron-monitor'],
    queryFn: async () => (await api.get<CronJob[]>('/cron-monitor')).data,
    refetchInterval: 60_000,
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <div className="text-center py-14 text-slate-500">
        <AlertTriangle size={26} className="mx-auto text-amber-500 mb-2" />
        Cron history is available to Super Admin and Admin users only.
      </div>
    );
  }

  const failing = jobs.filter((j) => j.failed24h > 0).length;
  const stale = jobs.filter((j) => !j.lastRun).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Clock size={18} /> Cron History</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Every background job, its schedule, last run and how many records it touched. Click a row for its run history.
          </p>
        </div>
        <button onClick={() => refetch()} className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 flex items-center gap-1.5">
          <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Jobs registered" value={jobs.length} />
        <Kpi label="Failing (24h)" value={failing} tone={failing ? 'rose' : undefined} />
        <Kpi label="Never run" value={stale} tone={stale ? 'amber' : undefined} />
      </div>

      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="w-8" />
              <th className="text-left px-3 py-2">Cron name</th>
              <th className="text-left px-3 py-2">Schedule</th>
              <th className="text-left px-3 py-2">Last run</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Duration</th>
              <th className="text-right px-3 py-2">Records affected</th>
              <th className="text-right px-3 py-2">Runs (24h)</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="text-center py-10 text-slate-400"><Loader2 size={18} className="mx-auto animate-spin" /></td></tr>}
            {!isLoading && jobs.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400">No jobs registered.</td></tr>}
            {jobs.map((j) => {
              const open = expanded === j.key;
              return (
                <Fragment key={j.key}>
                  <tr
                    className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${open ? 'bg-slate-50' : ''}`}
                    onClick={() => setExpanded(open ? null : j.key)}
                  >
                    <td className="px-2 text-slate-400">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{j.name}</div>
                      <div className="text-[11px] text-slate-500">{j.module}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      <div>{j.scheduleText}</div>
                      <code className="text-[10px] text-slate-400">{j.schedule}</code>
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      <div className={j.lastRun ? 'text-slate-800' : 'text-amber-700 font-medium'}>{fmtAgo(j.lastRun?.startedAt)}</div>
                      <div className="text-[11px] text-slate-500">{fmtWhen(j.lastRun?.startedAt)}</div>
                    </td>
                    <td className="px-3 py-2">{j.lastRun ? <StatusPill status={j.lastRun.status} /> : <span className="text-xs text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-right text-xs text-slate-600">{fmtDuration(j.lastRun?.durationMs)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-semibold text-slate-900">{j.lastRun ? j.lastRun.affected.toLocaleString('en-IN') : '—'}</div>
                      <div className="text-[10px] text-slate-500">{j.affected24h.toLocaleString('en-IN')} in 24h</div>
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      <span className="text-slate-800">{j.runs24h}</span>
                      {j.failed24h > 0 && <span className="ml-1 text-rose-600 font-medium">({j.failed24h} failed)</span>}
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-slate-50/60 border-t border-slate-100">
                      <td />
                      <td colSpan={7} className="px-3 py-3">
                        <p className="text-xs text-slate-600 mb-1">{j.description}</p>
                        <p className="text-[11px] text-slate-500 mb-3">
                          <b>Records affected</b> counts: {j.affectsLabel}. Last 7 days: {j.affected7d.toLocaleString('en-IN')}.
                        </p>
                        {j.lastRun?.note && (
                          <div className="text-[11px] bg-white border border-slate-200 rounded px-2 py-1.5 mb-3 text-slate-700">
                            <span className="text-slate-400 uppercase tracking-wide mr-1">Last run:</span>{j.lastRun.note}
                          </div>
                        )}
                        {j.lastRun?.errorMessage && (
                          <div className="text-[11px] bg-rose-50 border border-rose-200 rounded px-2 py-1.5 mb-3 text-rose-700 font-mono break-all">
                            {j.lastRun.errorMessage}
                          </div>
                        )}
                        <RunHistory jobKey={j.key} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The last N runs of one job — loaded only when its row is expanded. */
function RunHistory({ jobKey }: { jobKey: string }) {
  const { data, isLoading } = useQuery<{ runs: CronRun[] }>({
    queryKey: ['cron-monitor-history', jobKey],
    queryFn: async () => (await api.get(`/cron-monitor/${encodeURIComponent(jobKey)}/history`, { params: { limit: 30 } })).data,
  });

  if (isLoading) return <div className="text-xs text-slate-400 py-2"><Loader2 size={13} className="inline animate-spin mr-1" /> Loading run history…</div>;
  const runs = data?.runs ?? [];
  if (!runs.length) return <div className="text-xs text-slate-400 py-2">This job has not run yet.</div>;

  return (
    <div className="border border-slate-200 rounded bg-white overflow-x-auto">
      <table className="w-full text-xs min-w-[720px]">
        <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
          <tr>
            <th className="text-left px-2 py-1.5">Started</th>
            <th className="text-left px-2 py-1.5">Status</th>
            <th className="text-right px-2 py-1.5">Duration</th>
            <th className="text-right px-2 py-1.5">Scanned</th>
            <th className="text-right px-2 py-1.5">Affected</th>
            <th className="text-right px-2 py-1.5">Failed</th>
            <th className="text-left px-2 py-1.5">Detail</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="px-2 py-1.5 whitespace-nowrap text-slate-700">{fmtWhen(r.startedAt)}</td>
              <td className="px-2 py-1.5"><StatusPill status={r.status} /></td>
              <td className="px-2 py-1.5 text-right text-slate-600">{fmtDuration(r.durationMs)}</td>
              <td className="px-2 py-1.5 text-right text-slate-600">{r.scanned.toLocaleString('en-IN')}</td>
              <td className="px-2 py-1.5 text-right font-medium text-slate-900">{r.affected.toLocaleString('en-IN')}</td>
              <td className={`px-2 py-1.5 text-right ${r.failed ? 'text-rose-600 font-medium' : 'text-slate-400'}`}>{r.failed}</td>
              <td className="px-2 py-1.5 text-slate-600 max-w-[420px]">
                {r.errorMessage
                  ? <span className="text-rose-600 break-all">{r.errorMessage}</span>
                  : <span className="break-words">{r.note ?? '—'}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: 'rose' | 'amber' }) {
  const cls = tone === 'rose' ? 'text-rose-700' : tone === 'amber' ? 'text-amber-700' : 'text-slate-900';
  return (
    <div className="border border-slate-200 rounded-lg px-3 py-2.5">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
